import { type HTMLAttributes } from 'react';

import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/Tooltip';
import { ptBR } from '@/i18n/pt-BR';
import { fmtLR, fmtSupportIndex, lrLog10, lrVerbal, type LRPolarity } from '@/lib/lr';
import { cn } from '@/lib/utils';

/**
 * Pílula com cor por polaridade ENFSI + intensidade derivada de |log10(LR)|/5.
 *
 * Cores: vermelho (H2, heteroautoria), cinza (inconclusivo/desconhecido),
 * verde (H1, mesma autoria). Tom mais saturado = força maior.
 *
 * Acessibilidade: o valor numérico fica visível; o texto verbal completo é
 * exposto via `aria-label` + `<span class="sr-only">` para leitores de tela.
 * Hover/focus mostram o tooltip com `LR = <valor> · <verbal>`, usando o
 * primitive `Tooltip` (T07). `TooltipProvider` é montado em `App.tsx`.
 *
 * E39.F1/ADR-0054: quando `supportIndex` (−100..+100, resolvido pelo backend) é
 * informado, o tooltip ganha a leitura de bancada do índice de suporte (substitui
 * a intensidade %). NÃO é probabilidade e NÃO é recomputado no front — só exibido
 * quando o valor vem do backend (ex.: composto no cabeçalho do workspace).
 */
const POLARITY_BG: Record<LRPolarity, string> = {
  h1: 'bg-success-50 text-success-700 border-success/40',
  h2: 'bg-danger-50 text-danger-700 border-danger/40',
  inconclusive: 'bg-surface-2 text-fg-muted border-border',
  unknown: 'bg-surface-2 text-fg-muted border-border',
};

export interface LRPillProps extends HTMLAttributes<HTMLSpanElement> {
  lr: number | null | undefined;
  /**
   * Índice de suporte assinado −100..+100 resolvido pelo backend (E39.F1/ADR-0054). // ux-neutrality:allow doc-comment
   * Leitura de bancada; só exibida quando informada (sem recálculo no front). // ux-neutrality:allow doc-comment
   */
  supportIndex?: number | null;
}

export function LRPill({ lr, supportIndex, className, ...props }: LRPillProps) {
  const verbal = lrVerbal(lr);
  const log = lrLog10(lr);
  const numeric = fmtLR(lr);
  const baseText = log == null ? verbal.text : `LR = ${numeric} · ${verbal.text}`;
  const tooltipText =
    log != null && supportIndex != null
      ? `${baseText} · ${ptBR.workspaceCaso.supportIndexValueLabel} ${fmtSupportIndex(supportIndex)}`
      : baseText;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          className={cn(
            // Tag do Design System (reta, 11px, tracking 0.02em) com o dado
            // pericial em mono — como o LR da tabela na prancha 1B.
            'inline-flex items-center gap-1.5 border px-[10px] py-[3px] font-mono text-[11px] font-medium tracking-[0.02em]',
            POLARITY_BG[verbal.polarity],
            className,
          )}
          data-lr-polarity={verbal.polarity}
          role="img"
          aria-label={tooltipText}
          // Focável para que o Tooltip apareça via teclado também (T07).
          // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
          tabIndex={0}
          {...props}
        >
          <span aria-hidden>{numeric}</span>
          <span className="sr-only">{verbal.text}</span>
        </span>
      </TooltipTrigger>
      <TooltipContent>{tooltipText}</TooltipContent>
    </Tooltip>
  );
}
